import type { ReactNode, TableHTMLAttributes } from 'react'
import { useId } from 'react'

interface Props extends TableHTMLAttributes<HTMLTableElement> {
  children: ReactNode
  /** Use the dense (text-xs) variant. Defaults to false (text-sm). */
  dense?: boolean
  /** Tailwind min-width hint to force horizontal scroll on narrow viewports. */
  minWidth?: string
  /** Extra classes for the outer scroll container. */
  containerClassName?: string
}

/**
 * Standard table layout: a horizontally-scrolling container wrapping a full-width table.
 * Use directly with native <thead>/<tbody>/<tr>/<th>/<td> children — cells are too varied
 * across views to warrant primitives.
 */
export function Table({
  children,
  dense = false,
  minWidth,
  className,
  containerClassName,
  ...rest
}: Props) {
  const sizeClass = dense ? 'text-xs' : 'text-sm'
  const widthClass = minWidth ?? 'min-w-[640px]'
  const id = useId()
  return (
    <div className={containerClassName ?? ''}>
      <table
        id={id}
        className={`w-full ${widthClass} ${sizeClass} ${className ?? ''}`.trim()}
        {...rest}
      >
        {children}
      </table>
      <style>{`
        table#${id} th,
        table#${id} td {
          white-space: nowrap !important;
        }
      `}</style>
    </div>
  )
}
